import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function LoginButton({ isNewNavbar = false }) {
    const { isAuthenticated, currentUser, logout } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [menuLinks, setMenuLinks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch menu links when logged in and dropdown is opened
    useEffect(() => {
        if (isAuthenticated && isOpen && menuLinks.length === 0) {
            fetchMenuLinks();
        }
    }, [isAuthenticated, isOpen]);

    const fetchMenuLinks = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/v1/client', {
                method: 'GET',
                credentials: 'include',
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.links) {
                    setMenuLinks(data.links);
                } else {
                    // Default links if none are provided by API
                    setMenuLinks([
                        { name: 'Личный кабинет', url: '/client' },
                        { name: 'Настройки', url: '/client/settings' },
                    ]);
                }
            } else {
                // Fallback to default links
                setMenuLinks([
                    { name: 'Личный кабинет', url: '/client' },
                    { name: 'Настройки', url: '/client/settings' },
                ]);
            }
        } catch (error) {
            console.error('Error fetching menu links:', error);
            // Fallback to default links
            setMenuLinks([
                { name: 'Личный кабинет', url: '/client' },
                { name: 'Настройки', url: '/client/settings' },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginClick = () => {
        if (isAuthenticated) {
            setIsOpen(!isOpen);
        } else {
            navigate('/login');
        }
    };

    const handleLogout = async () => {
        const result = await logout();
        if (result.success) {
            setIsOpen(false);
            navigate('/');
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={handleLoginClick}
                className={`flex items-center text-base font-bold ${isNewNavbar ? 'font-inter text-[#626fb0]' : "font-['Open Sans'] text-white"} py-2 ${isAuthenticated ? 'bg-transparent' : ''}`}
            >
                {isAuthenticated ? 'ЛК' : 'Вход'}
                {isAuthenticated && (
                    <svg xmlns="http://www.w3.org/2000/svg" className={`ml-1 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                )}
            </button>
            
            {isAuthenticated && isOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1 border-b" role="none">
                        <div className="block px-4 py-2 text-sm text-gray-700">
                            <div className="font-semibold">
                                {currentUser?.username || currentUser?.login || currentUser?.email || 'Пользователь'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                                {currentUser?.email || ''}
                            </div>
                        </div>
                    </div>
                    
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {isLoading ? (
                            <div className="px-4 py-2 text-sm text-gray-500">
                                Загрузка...
                            </div>
                        ) : (
                            <>
                                {menuLinks.map((link, index) => (
                                    <Link 
                                        key={index}
                                        to={link.url} 
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        role="menuitem"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                                
                                <button 
                                    onClick={handleLogout}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                >
                                    Выйти
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}